import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { CSVLink } from 'react-csv';

import { agentReportDetailsGet } from '../../../store/slices/tenantSlices/agentPerformanceSlice';
import CallexLoader from '../../../components/CallexLoader';
import { calculateDurations } from '../../../utils/common';

const DailyDetailedReportModal = ({ isOpen, onClose, data, canExport }) => {
  const dispatch = useDispatch();

  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const excelRef = useRef();
  console.log('data', data)
  const getAgentReportDetails = async () => {
    setIsLoading(true);
    try {
      const processId = String(data?.process_id)
        ?.split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '')
        .map(Number);

      const isDateValid = data?.date && data?.date !== 'TOTAL';

      const payload = {
        from_date: isDateValid ? `${data.date} 00:00:01` : data?.from_date,
        to_date: isDateValid ? `${data.date} 23:59:59` : data?.to_date,
        user_id: data?.user_id,
        archiveId: data?.archiveId,
        process_id: processId
      };
      const response = await dispatch(agentReportDetailsGet(payload)).unwrap();
      if (response && response.success) {
        setReportData(response.data);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getAgentReportDetails();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isCsvDownload && csvReportData.length > 0) {
      excelRef?.current?.link?.click();
      setIsCsvDownload(false);
    }
  }, [isCsvDownload, csvReportData]);

  const { durations, totalDuration } = calculateDurations(reportData || []);

  const handleExportCSV = () => {
    if (!canExport) return;
    const headers = [
      { label: 'User', key: 'user' },
      { label: 'Process', key: 'process' },
      { label: 'Entry Time', key: 'entry_time' },
      { label: 'End Time', key: 'end_time' },
      { label: 'Break', key: 'break' },
      { label: 'Duration', key: 'duration' }
    ];

    const csvData = reportData.map((val, index) => ({
      user: data?.user,
      process: data?.Process,
      entry_time: val?.break_name ? val?.break_in || '-' : val?.login || '-',
      end_time: val?.break_name ? val?.break_out || '-' : val?.logout || '-',
      break: val?.break_name || '-',
      duration: durations[index]?.formattedDuration || '-'
    }));

    // Add Total row
    csvData.push({
      user: 'TOTAL',
      process: '',
      entry_time: '',
      end_time: '',
      break: '',
      duration: totalDuration
    });

    setCsvHeaderData(headers);
    setCsvReportData(csvData);
    setIsCsvDownload(true);
  };

  const handleCloseModal = () => {
    setReportData([]);
    setIsLoading(true);
    setIsCsvDownload(false);
    setCsvReportData([]);
    setCsvHeaderData([]);
    onClose();
  };

  return (
    <div>
      <Modal isOpen={isOpen} size="lg">
        <ModalHeader className="lead-retries-report-hedaer">
          <span className="text-primary text-uppercase fw-bold">Daily Detailed Report</span>
          <div className="d-flex align-items-center gap-2">
            {canExport && (
              <div
                className={`bg-success rounded-2 d-flex justify-content-center align-items-center ${isLoading || reportData.length === 0 ? 'callex-disabled' : ''
                  }`}
                style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                onClick={handleExportCSV}>
                <Icon.Download size={20} className="text-white" />
              </div>
            )}
            {isCsvDownload && (
              <CSVLink
                data={csvReportData}
                headers={csvHeaderData}
                filename={`Daily_Detailed_Report_${data?.user || 'Agent'}.csv`}
                ref={excelRef}
                className="d-none"
                target="_blank"
              />
            )}
          </div>
        </ModalHeader>
        <ModalBody className="p-0" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <CallexLoader />
            </div>
          ) : (
            <div
              className="daily-report-table"
              style={{ maxHeight: 'calc(100vh - 232px)' }}>
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Process</th>
                    <th>Entry Time</th>
                    <th>End Time</th>
                    <th>Break</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.map((val, index) => {
                    const breakName = val?.break_name || '-';
                    return (
                      <tr key={index}>
                        <td>{data?.user}</td>
                        <td>{data?.Process}</td>
                        <td>{val?.break_name ? val?.break_in || '-' : val?.login || '-'}</td>
                        <td>{val?.break_name ? val?.break_out || '-' : val?.logout || '-'}</td>
                        <td>{breakName}</td>
                        <td>{durations[index].formattedDuration}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#2cd07e33' }}>
                    <td colSpan="5">TOTAL</td>
                    <td>{totalDuration}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

DailyDetailedReportModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.object,
  canExport: PropTypes.bool
};

export default DailyDetailedReportModal;
